import moment from 'moment';
import React from 'react';
import {
  useTable,
  useSortBy,
  usePagination,
} from 'react-table';
import styled from 'styled-components';

import useAPI from '@hooks/useAPI';
import Spinner from '@shared/Spinner';

const listAccessor = ({
  screen_type,
  rule_name,
  updatedAt,
  created_at,
  rule_description,
}) => ({
  description: rule_description,
  ruleType: screen_type,
  label: rule_name,
  updatedAt,
  created_at,
});

const FullWidthContainer = styled.div`
  width: 100%;
`;
const FullWidthTable = styled.table`
  width: 100%;
  th {
    text-align: left;
  }
`;
const TableBodyContainer = styled.tbody``;
const Row = styled.tr`
  :not(:last-child) {
    border-bottom: ${props => props.theme.borderMd};
  }
`;
const CellContainer = styled.td`
  padding: 15px 0px 10px 0px;
  vertical-align: middle;
`;

const Spacer = styled.span`
  border-left: ${props => props.theme.borderMd};
  height: 20px;
  padding: 0px 10px;
`;

const TypeCell = styled.span`
  text-transform: uppercase;
  color: ${props => props.theme.primary};
  font-size: ${props => props.theme.font.fontSm};
`;

const AboveLabel = styled.span`
  font-size: ${props => props.theme.font.fontSm};
  color: ${props => props.theme.fontTertiary};
  text-transform: capitalize;
`;

const Label = styled.span`
  line-height: 24px;
  color: ${props => props.theme.primary};
`;

const PaginationStyle = styled.div`
  margin: 40px 20%;
  max-width: 700px;
  width: 700px;

  select,
  span {
    margin: 0 10px;
  }

  select {
    outline: none;
    border-radius 2px;
  }

  button {
    margin: 0 10px;
    font-size: 1.03rem;
    padding: 7px 7px;
    color: #7b9095;
    border: none;
    outline: none;
    border-radius: 10px;
    background: #c6d2db;
  }

  .btn__next:hover {
    color: ${props => props.canNextPage && 'white'};
    background: ${props => props.canNextPage && '#7b9095'};
  }

  .btn__prev:hover {
    color: ${props => props.canPreviousPage && 'white'};
    background: ${props => props.canPreviousPage && '#7b9095'};
  }
`;

export default function RulesTable() {
  const screenConfig = useAPI('get', 'ScreenConsoleApi', `config`);
  const { isLoading } = screenConfig;
  const data = screenConfig?.response?.config_screen?.rules || [];
  const hasResults = data && data.length;

  if (isLoading) {
    return (
      <FullWidthContainer>
        <Spinner center />
      </FullWidthContainer>
    );
  }

  if (!isLoading && !hasResults) {
    return (
      <div>
        No configuration present. Please contact{' '}
        <a href='mailto:screen-support@enigma.com'>screen-support@enigma.com</a>{' '}
        to update your configuration.
      </div>
    );
  }

  return <RulesTableContent data={data} />;
}

const RulesTableContent = ({ data }) => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'Rule Name',
        Cell: ({ cell: { value } }) => (
          <span>
            <AboveLabel>
              Last Updated{' '}
              {moment(value.updatedAt || value.created_at).format('MM/DD/YYYY')}
              <br />
            </AboveLabel>
            <Label>{value.label}</Label>
            <br />
            <AboveLabel>
              {value.ruleType} - {value.description}
            </AboveLabel>
          </span>
        ),
        accessor: listAccessor,
      },
      {
        Header: 'Created',
        id: 'created_at',
        Cell: ({ cell: { value } }) => (
          <span>{moment(value).format('YYYY-MM-DDTHH:mm:ss')}</span>
        ),
        accessor: 'created_at',
      },
      {
        Header: 'Type',
        Cell: ({ cell: { value } }) => <TypeCell>{value}</TypeCell>,
        accessor: 'screen_type',
      },
    ],
    []
  );

  const initialState = {
    sortBy: [{ id: 'created_at', desc: true }],
    pageSize: 10,
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state,
    prepareRow,
    page,
    pageCount,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
  } = useTable(
    { columns, data: React.useMemo(() => data, [data]), initialState },
    useSortBy,
    usePagination
  );

  const { pageIndex, pageSize } = state;

  return (
    <>
      <FullWidthTable {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  <span>
                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <TableBodyContainer {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <Row {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <CellContainer {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </CellContainer>
                  );
                })}
              </Row>
            );
          })}
        </TableBodyContainer>
      </FullWidthTable>
      <PaginationStyle
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
      >
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{' '}
        </span>
        <select
          value={pageSize}
          onChange={event => setPageSize(Number(event.target.value))}
        >
          {[10, 20, 50].map(pageSize => {
            return (
              <option value={pageSize} key={pageSize}>
                Show {pageSize}
              </option>
            );
          })}
        </select>
        <button
          className='btn__prev'
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          Previous Page
        </button>
        <button
          className='btn__next'
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          Next Page
        </button>
      </PaginationStyle>
    </>
  );
};
